import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { allColors, AllColors, Colors } from "src/utils/ThemeColors";
import { getRoleKey } from "src/network/User";
import { hexToHSL, HSLToHex, TransformColors } from "src/utils/Colors";
import { NetworkConfig } from "src/network/NetworkConfig";
import { MessageType } from "src/components/notifications/Notifier";
import { Colorpicker } from "src/screens/dashboard/customers/customer/design/Colorpicker";
import styles from "./CustomerDesign.module.css";

interface CustomerDesignProps {
    customerId: string;
    hostname: string;
}

const maxDataSize = 1000000;
const dataTypes = ["image/jpeg", "image/png"];

const normalizeColor = (value: string | undefined): string => {
    if (!value) {
        return "#58697a";
    }
    const cleanedValue = value.replace("!important", "").trim();
    if (cleanedValue.startsWith("#")) {
        return cleanedValue;
    }
    const match = cleanedValue.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (!match) {
        return cleanedValue;
    }
    const [r, g, b] = match.slice(1, 4).map((item) => parseInt(item, 10));
    const toHex = (channel: number) => channel.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const extractCssVariable = (css: string, variableName: string): string | undefined => {
    const regex = new RegExp(`--${variableName}\\s*:\\s*([^;]+);`, "i");
    const match = css.match(regex);
    return match ? match[1].trim() : undefined;
};

const CustomerDesignBase = (props: CustomerDesignProps) => {
    const { authStore, uiStore } = useContext(RootStoreContext);
    const [selectedKey, setSelectedKey] = useState<string>("custom");
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [reloadNotification, setReloadNotification] = useState<boolean>(false);
    const [isColorUnavailable, setIsColorUnavailable] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [logoUrl, setLogoUrl] = useState<string>("");
    const [fileBase64, setFileBase64] = useState<string | undefined>(undefined);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const [customColors, setCustomColors] = useState<Colors>({
        primary: "#58697a",
        primary100: "#58697a",
        primary400: "#343f49",
        primary900: "#232a30",
        additionalColor: "#606366",
        additionalColor2: "#00254d"
    });

    const designBaseUrl = useMemo(() => `${props.hostname}/api/designs`, [props.hostname]);
    const uploadLogoUrl = useMemo(() => NetworkConfig.brandAssets.uploadLogo, []);
    const uploadColorsUrl = useMemo(() => NetworkConfig.brandAssets.uploadColors, []);

    const colorOptions: { key: keyof AllColors; colors: Colors }[] = useMemo(
        () =>
            Object.keys(allColors).map((key) => {
                const colors = allColors[key as keyof AllColors];
                return { key: key as keyof AllColors, colors };
            }),
        []
    );

    useEffect(() => {
        setLogoUrl(`${designBaseUrl}/logo/brand.jpg?ts=${Date.now()}`);
    }, [designBaseUrl]);

    useEffect(() => {
        const loadCustomerColors = async () => {
            try {
                const response = await fetch(`${designBaseUrl}/css/brand.css?ts=${Date.now()}`);
                if (!response.ok) {
                    return;
                }
                const css = await response.text();

                const colors: Colors = {
                    primary: normalizeColor(extractCssVariable(css, "primary")),
                    primary100: normalizeColor(extractCssVariable(css, "primary100")),
                    primary400: normalizeColor(extractCssVariable(css, "primary400")),
                    primary900: normalizeColor(extractCssVariable(css, "primary900")),
                    additionalColor: normalizeColor(extractCssVariable(css, "additionalColor")),
                    additionalColor2: normalizeColor(extractCssVariable(css, "additionalColor2"))
                };

                for (const option of colorOptions) {
                    if (
                        colors.primary.toLowerCase() === option.colors.primary.toLowerCase() &&
                        colors.primary100.toLowerCase() === option.colors.primary100.toLowerCase() &&
                        colors.primary400.toLowerCase() === option.colors.primary400.toLowerCase() &&
                        colors.primary900.toLowerCase() === option.colors.primary900.toLowerCase()
                    ) {
                        setSelectedKey(option.key);
                        setCustomColors(colors);
                        return;
                    }
                }

                setSelectedKey("custom");
                setCustomColors(colors);
            } catch (error) {
                setErrorMessage(`Farben konnten nicht geladen werden: ${error}`);
            }
        };

        loadCustomerColors();
    }, [colorOptions, designBaseUrl]);

    const onFormChange = useCallback((event: any) => {
        setSelectedKey(event.currentTarget.value);
    }, []);

    const onChangeColorField = useCallback((newColor: string) => {
        const hslColor = hexToHSL(newColor);

        setIsColorUnavailable(false);
        if (hslColor.l >= 0 && hslColor.l <= 15) {
            hslColor.l = 16;
            setIsColorUnavailable(true);
        } else if (hslColor.l >= 91 && hslColor.l <= 100) {
            hslColor.l = 90;
            setIsColorUnavailable(true);
        }

        const colors = TransformColors({ h: hslColor.h, s: hslColor.s, l: hslColor.l });
        const newColors: Colors = {
            primary: newColor,
            primary100: HSLToHex(colors[3].h, colors[3].s, colors[3].l),
            primary400: HSLToHex(colors[2].h, colors[2].s, colors[2].l),
            primary900: HSLToHex(colors[1].h, colors[1].s, colors[1].l),
            additionalColor: HSLToHex(colors[4].h, colors[4].s, colors[4].l),
            additionalColor2: HSLToHex(colors[5].h, colors[5].s, colors[5].l)
        };

        setCustomColors(newColors);
        setSelectedKey("custom");
    }, []);

    const onSaveColors = useCallback(async () => {
        setIsUpdating(true);

        const colors =
            selectedKey === "custom" ? customColors : colorOptions.find((k) => k.key === selectedKey)?.colors;

        if (!colors) {
            setErrorMessage("Farben konnten nicht gespeichert werden.");
            setIsUpdating(false);
            return;
        }

        if (authStore.user && authStore.token) {
            try {
                const accessToken = authStore.token;
                const tokenType = authStore.tokenType;
                const role = authStore.user?.role;

                const response = await fetch(`${uploadColorsUrl}/${props.customerId}`, {
                    method: "PUT",
                    body: JSON.stringify({
                        primary: colors.primary,
                        primary100: colors.primary100,
                        primary400: colors.primary400,
                        primary900: colors.primary900,
                        additionalcolor: colors.additionalColor,
                        additionalcolor2: colors.additionalColor2
                    }),
                    headers: {
                        Authorization: `${tokenType} ${accessToken}`,
                        "Content-Type": "application/json",
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (!response.ok) {
                    const responseText = await response.text();
                    setErrorMessage(`Beim Speichern der Farben ist ein Fehler aufgetreten: ${responseText}`);
                } else {
                    setReloadNotification(true);
                    uiStore.printStatusMessage("Farben erfolgreich gespeichert", MessageType.INFO);
                }
            } catch (error) {
                setErrorMessage(`Beim Speichern der Farben ist ein Fehler aufgetreten: ${error}`);
            } finally {
                setIsUpdating(false);
            }
        } else {
            setIsUpdating(false);
            setErrorMessage("Keine Authorisierungs-Informationen vorhanden.");
        }
    }, [
        authStore.token,
        authStore.tokenType,
        authStore.user,
        colorOptions,
        customColors,
        props.customerId,
        selectedKey,
        uiStore,
        uploadColorsUrl
    ]);

    const handleChangeInput = useCallback(
        async (event: any) => {
            const toBase64 = (file: Blob) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => {
                        resolve(reader.result?.toString().split(",")[1]);
                    };
                    reader.onerror = (error) => reject(error);
                });

            const newFile = event.target.files[0];
            if (!newFile) {
                return;
            }

            if (newFile.size > maxDataSize) {
                setWarningMessage(`Datei zu gross. Maximal ${(maxDataSize / 1000000).toString()} MB.`);
                return;
            }
            if (dataTypes.indexOf(newFile.type) === -1) {
                setWarningMessage("Nur JPEG oder PNG erlaubt.");
                return;
            }

            await toBase64(newFile).then((f) => {
                setWarningMessage(null);
                setFileBase64(f as string);
            });
        },
        [setFileBase64, setWarningMessage]
    );

    const onUploadLogo = useCallback(async () => {
        setIsUploading(true);
        if (fileBase64 && authStore.user && authStore.token) {
            try {
                const accessToken = authStore.token;
                const tokenType = authStore.tokenType;
                const role = authStore.user?.role;

                const response = await fetch(`${uploadLogoUrl}/${props.customerId}`, {
                    method: "PUT",
                    body: JSON.stringify({ file: fileBase64 }),
                    headers: {
                        Authorization: `${tokenType} ${accessToken}`,
                        "Content-Type": "application/json",
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (!response.ok) {
                    const responseText = await response.text();
                    setWarningMessage(`Beim Upload ist ein Fehler aufgetreten: ${responseText}`);
                } else {
                    setLogoUrl(`${designBaseUrl}/logo/brand.jpg?ts=${Date.now()}`);
                    setFileBase64(undefined);
                }
            } catch (error) {
                setWarningMessage(`Beim Upload ist ein Fehler aufgetreten: ${error}`);
            } finally {
                setIsUploading(false);
            }
        } else {
            setIsUploading(false);
            setWarningMessage("Keine Authorisierungs-Informationen vorhanden.");
        }
    }, [
        authStore.token,
        authStore.tokenType,
        authStore.user,
        designBaseUrl,
        fileBase64,
        props.customerId,
        uploadLogoUrl
    ]);

    const renderPalette = (colors: Colors, custom?: boolean) => (
        <div className={styles.paletteRow}>
            {custom ? (
                <div className={styles.customSwatch}>
                    <Colorpicker value={normalizeColor(colors.primary)} onChange={onChangeColorField} />
                </div>
            ) : (
                <div className={styles.swatch} style={{ background: colors.primary }} />
            )}
            <div className={styles.swatch} style={{ background: colors.primary900 }} />
            <div className={styles.swatch} style={{ background: colors.primary400 }} />
            <div className={styles.swatch} style={{ background: colors.primary100 }} />
            {colors.additionalColor ? (
                <div className={styles.swatch} style={{ background: colors.additionalColor }} />
            ) : undefined}
            {colors.additionalColor2 ? (
                <div className={styles.swatch} style={{ background: colors.additionalColor2 }} />
            ) : undefined}
            <div className={styles.swatch} style={{ background: "#ffffff" }}>
                <UI.Icon icon={UI.SVGIcon.Favicon} color={colors.primary} />
            </div>
        </div>
    );

    return (
        <UI.Row className={styles.designRow}>
            <UI.Col md={6}>
                <UI.Card title="Logo">
                    <div className={styles.logoContainer}>
                        <img src={logoUrl} alt="Customer Logo" className={styles.logoImage} />
                    </div>
                    <input
                        className={`${styles.fileInput} mt-2 mb-3`}
                        type="file"
                        onChange={handleChangeInput}
                        accept={dataTypes.join(",")}
                    />
                    <UI.Button
                        label={isUploading ? "Wird hochgeladen" : "Hochladen"}
                        onClick={onUploadLogo}
                        disabled={!fileBase64 || isUploading}
                        className="w-100"
                    />
                    {warningMessage && (
                        <UI.Alert variant="warning" className="mt-3">
                            {warningMessage}
                        </UI.Alert>
                    )}
                </UI.Card>
            </UI.Col>
            <UI.Col md={6}>
                <UI.Card title="Farbauswahl">
                    <UI.Form className={styles.colorSelectionForm}>
                        {colorOptions.map(({ key, colors }) => (
                            <UI.Form.Check
                                key={`option-${key}`}
                                className={`d-flex align-items-center mb-2 ${styles.colorOptionRow}`}
                            >
                                <UI.Form.Check.Input
                                    className="mt-0"
                                    type={"radio"}
                                    name="customerDesignPalette"
                                    id={`palette-${key}`}
                                    checked={selectedKey === key}
                                    value={key}
                                    onChange={onFormChange}
                                />
                                <UI.Form.Label className={styles.paletteLabel} htmlFor={`palette-${key}`}>
                                    {renderPalette(colors, false)}
                                </UI.Form.Label>
                            </UI.Form.Check>
                        ))}
                        <UI.Form.Check key={"option-custom"} className={`d-flex align-items-center mb-2 ${styles.colorOptionRow}`}>
                            <UI.Form.Check.Input
                                className="mt-0"
                                type={"radio"}
                                name="customerDesignPalette"
                                id="palette-custom"
                                checked={selectedKey === "custom"}
                                value={"custom"}
                                onChange={onFormChange}
                            />
                            <UI.Form.Label className={styles.paletteLabel} htmlFor="palette-custom">
                                {renderPalette(
                                    {
                                        primary: customColors.primary,
                                        primary100: customColors.primary100,
                                        primary400: customColors.primary400,
                                        primary900: customColors.primary900,
                                        additionalColor: customColors.additionalColor,
                                        additionalColor2: customColors.additionalColor2
                                    },
                                    true
                                )}
                            </UI.Form.Label>
                        </UI.Form.Check>

                        <UI.Button
                            label="Speichern"
                            onClick={onSaveColors}
                            disabled={isUpdating || isColorUnavailable}
                            className="mt-3 w-100"
                        />
                        {isColorUnavailable ? (
                            <UI.Alert variant="warning" className="mt-3">
                                Die gewaehlte Farbe ist zu hell oder zu dunkel.
                            </UI.Alert>
                        ) : undefined}
                        {errorMessage ? (
                            <UI.Alert variant="warning" className="mt-3">
                                {errorMessage}
                            </UI.Alert>
                        ) : undefined}
                        {reloadNotification && (
                            <UI.Alert variant="warning" className="mt-2">
                                Browser neu laden: Ctrl + F5 (Windows) oder Cmd + Shift + R (Mac).
                            </UI.Alert>
                        )}
                    </UI.Form>
                </UI.Card>
            </UI.Col>
        </UI.Row>
    );
};

export const CustomerDesign = Sentry.withProfiler(CustomerDesignBase);
